import {
  createContext,
  type FC,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { Virtualizer } from '@tanstack/react-virtual';
import type { UserApiId } from '@/model/User.model';
import { useDataTable } from '@/components/UI/DataTableV2/DataTableProvider';
import { type DataTableCellSelectionChangeEvent, DataTableEvent } from '@/components/UI/DataTableV2/DataTable.model';
import { type RemoteSelectionsModel, useRemoteSelections } from './hooks/useRemoteSelections';

interface RemoteSelectionInteractionState {
  hoveredUserId: UserApiId | null;
  setHoveredUserId: (id: UserApiId | null) => void;
  pulsedUserId: UserApiId | null;
  pulseUserSelection: (userId: UserApiId) => void;
  followedUserId: UserApiId | null;
  toggleFollowUser: (userId: UserApiId) => void;
  selections: RemoteSelectionsModel;
}

const RemoteSelectionInteractionContext = createContext<RemoteSelectionInteractionState | undefined>(undefined);

interface RemoteSelectionInteractionProviderProps {
  children: ReactNode;
  rowVirtualizer: Virtualizer<HTMLDivElement, Element>;
  pinnedColumnCount: number;
}

const PULSE_DURATION_MS = 1000;

/**
 * Provides interaction state for remote user selections: avatar hover pulse,
 * follow-focus mode (click an avatar to jump to and track a user's selection),
 * and the memoised selection model consumed by the overlay.
 */
export const RemoteSelectionInteractionProvider: FC<RemoteSelectionInteractionProviderProps> = ({
  children,
  rowVirtualizer,
  pinnedColumnCount,
}) => {
  const { dataTableService } = useDataTable();
  const [hoveredUserId, setHoveredUserId] = useState<UserApiId | null>(null);
  const [pulsedUserId, setPulsedUserId] = useState<UserApiId | null>(null);
  const [followedUserId, setFollowedUserId] = useState<UserApiId | null>(null);
  const selections = useRemoteSelections();
  const pulseTimeoutRef = useRef<number | undefined>(undefined);

  const scrollToCoordinate = useCallback(
    (rowIndex: number, colIndex: number) => {
      // We drive the scroll container directly rather than virtualizer
      // scrollToIndex: its smooth scroll stalls when the target index is far
      // outside the rendered range (it re-measures mid-animation and never
      // arrives). Cells are fixed-size, so the exact pixel offset is known and a
      // native smooth scrollTo reliably reaches it regardless of distance.
      const scrollElement = rowVirtualizer.scrollElement;
      if (scrollElement === null) {
        return;
      }

      const { cellWidth, cellHeight } = dataTableService.dimensions;

      // Keep-in-view: only move along an axis when the cell is off-screen, and
      // only far enough to bring it back to the nearest edge.
      const keepInView = (start: number, size: number, viewStart: number, viewSize: number): number => {
        const end = start + size;
        const viewEnd = viewStart + viewSize;
        if (start < viewStart) {
          return start;
        }
        if (end > viewEnd) {
          return end - viewSize;
        }
        return viewStart;
      };

      const targetTop = keepInView(
        rowIndex * cellHeight,
        cellHeight,
        scrollElement.scrollTop,
        scrollElement.clientHeight
      );

      const adjustedColIndex = colIndex - pinnedColumnCount;
      const targetLeft =
        adjustedColIndex >= 0
          ? keepInView(adjustedColIndex * cellWidth, cellWidth, scrollElement.scrollLeft, scrollElement.clientWidth)
          : scrollElement.scrollLeft;

      scrollElement.scrollTo({ top: targetTop, left: targetLeft, behavior: 'smooth' });
    },
    [rowVirtualizer, pinnedColumnCount, dataTableService]
  );

  const pulse = useCallback((userId: UserApiId) => {
    if (pulseTimeoutRef.current !== undefined) {
      window.clearTimeout(pulseTimeoutRef.current);
    }
    setPulsedUserId(userId);
    pulseTimeoutRef.current = window.setTimeout(() => {
      setPulsedUserId(null);
      pulseTimeoutRef.current = undefined;
    }, PULSE_DURATION_MS);
  }, []);

  const pulseUserSelection = useCallback(
    (userId: UserApiId) => {
      if (!selections.byUser.has(userId)) {
        return;
      }
      pulse(userId);
    },
    [selections, pulse]
  );

  const toggleFollowUser = useCallback((userId: UserApiId) => {
    setFollowedUserId((current) => (current === userId ? null : userId));
  }, []);

  const followedCellRef = followedUserId !== null ? selections.userToCellRef.get(followedUserId) : undefined;

  useEffect(() => {
    if (followedUserId === null || followedCellRef === undefined) {
      return;
    }
    const selection = selections.byUser.get(followedUserId);
    if (selection === undefined) {
      return;
    }
    const colIndex = dataTableService.toIndexColumn(selection.fromCoordinate.column);
    const rowIndex = dataTableService.toIndexRow(selection.fromCoordinate.row);
    if (colIndex === undefined || rowIndex === undefined) {
      return;
    }
    scrollToCoordinate(rowIndex, colIndex);
    // followedUserId entering follow mode, or followedCellRef changing, signals
    // we should (re)jump to the followed user's current selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [followedUserId, followedCellRef]);

  useEffect(() => {
    const abortController = new AbortController();
    const handler = (event: DataTableCellSelectionChangeEvent): void => {
      if (event.detail.source === 'user') {
        setFollowedUserId(null);
      }
    };
    dataTableService.subscribe(DataTableEvent.CellSelectionChange, handler, abortController.signal);
    return () => {
      abortController.abort('teardown');
    };
  }, [dataTableService]);

  useEffect(
    () => () => {
      if (pulseTimeoutRef.current !== undefined) {
        window.clearTimeout(pulseTimeoutRef.current);
      }
    },
    []
  );

  const value = useMemo(
    () => ({
      hoveredUserId,
      setHoveredUserId,
      pulsedUserId,
      pulseUserSelection,
      followedUserId,
      toggleFollowUser,
      selections,
    }),
    [hoveredUserId, pulsedUserId, pulseUserSelection, followedUserId, toggleFollowUser, selections]
  );

  return (
    <RemoteSelectionInteractionContext.Provider value={value}>{children}</RemoteSelectionInteractionContext.Provider>
  );
};

export const useRemoteSelectionInteraction = (): RemoteSelectionInteractionState => {
  const context = useContext(RemoteSelectionInteractionContext);
  if (context === undefined) {
    throw new Error('useRemoteSelectionInteraction must be used within RemoteSelectionInteractionProvider');
  }
  return context;
};
